import React, { useEffect, useRef, useState } from 'react';
import { DEFAULT_MODULE_GAP, LAYOUT_BOUNDS, PAPER_DIMENSIONS, type PdfLayoutOverrides, type PdfPaperSize } from '../tokens/pdfLayout';
import { page as pageDefaults } from '../tokens/designTokens';

// Opt-in layout settings popover. Lives next to the Download button. Writes
// into the drawer's `layoutOverrides` state which flows into ReportDocument
// + ReportPreview. Defaults are read from the same tokens the renderers use
// at default-time, so the inputs reflect what production would render with
// no overrides set, and clearing a field reverts to that default exactly.
//
// Self-contained styling (no SCSS module) — the feature is scoped, may be
// reverted, and the visual treatment is intentionally minimal so it reads
// as a debug surface rather than a polished product feature.

type Props = {
    value: PdfLayoutOverrides;
    onChange: (next: PdfLayoutOverrides) => void;
    /** READ-only mode: render the trigger button greyed out, non-clickable.
     *  The drawer flips this on when the visibility config sets
     *  `aicaReport.customLayout` to 0 (show but don't allow edits). */
    disabled?: boolean;
};

const PAPER_OPTIONS: PdfPaperSize[] = ['A4', 'A3', 'LETTER', 'LEGAL', 'TABLOID'];

const ROUND = (n: number) => Math.round(n);

// The bounds prevent header/footer absolute positioning from breaking — a
// padding under 16pt collides with the running header, over 100pt eats the
// title. Module gap caps at 60pt because anything larger never packs more
// than one module per page anyway.
const PADDING_DEFAULTS = {
    paddingTop: ROUND(pageDefaults.paddingTop),
    paddingRight: ROUND(pageDefaults.paddingRight),
    paddingBottom: ROUND(pageDefaults.paddingBottom),
    paddingLeft: ROUND(pageDefaults.paddingLeft),
};

const isAnythingSet = (v: PdfLayoutOverrides): boolean =>
    v.paperSize != null ||
    v.paddingTop != null ||
    v.paddingRight != null ||
    v.paddingBottom != null ||
    v.paddingLeft != null ||
    v.moduleGap != null;

export const LayoutSettingsPopover: React.FC<Props> = ({ value, onChange, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    // Click-outside + Esc to close. Standard popover hygiene; without this the
    // open panel hangs around when the user clicks back into the rail or the
    // preview, which reads as broken interaction.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const update = (patch: Partial<PdfLayoutOverrides>) => {
        const next: PdfLayoutOverrides = { ...value, ...patch };
        // Drop undefined keys so `hasLayoutOverrides` reads true only when
        // something is actually set, keeping the fast-path intact.
        (Object.keys(next) as Array<keyof PdfLayoutOverrides>).forEach((k) => {
            if (next[k] === undefined || (typeof next[k] === 'number' && Number.isNaN(next[k]))) delete next[k];
        });
        onChange(next);
    };

    const reset = () => onChange({});
    const overridesActive = isAnythingSet(value);

    const dim = value.paperSize ? PAPER_DIMENSIONS[value.paperSize] : PAPER_DIMENSIONS.A4;

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                disabled={disabled}
                style={{
                    height: 36,
                    padding: '0 14px',
                    marginRight: 8,
                    background: disabled ? '#F3F4F6' : overridesActive ? '#EEF2FF' : '#FFFFFF',
                    border: `1px solid ${disabled ? '#D1D5DB' : overridesActive ? '#6366F1' : '#D1D5DB'}`,
                    borderRadius: 6,
                    color: disabled ? '#9CA3AF' : overridesActive ? '#4338CA' : '#374151',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.7 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                }}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-disabled={disabled}
            >
                Custom PDF Layout
                {overridesActive ? (
                    <span
                        style={{
                            background: '#6366F1',
                            color: '#FFF',
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '1px 5px',
                            borderRadius: 999,
                            letterSpacing: 0.2,
                        }}
                    >
                        ON
                    </span>
                ) : null}
            </button>

            {open ? (
                <div
                    role="dialog"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        width: 320,
                        background: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: 8,
                        boxShadow: '0 10px 32px rgba(0,0,0,0.12)',
                        padding: 14,
                        zIndex: 1000,
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Adjust PDF Layout </div>
                        <button
                            type="button"
                            onClick={reset}
                            disabled={!overridesActive}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: overridesActive ? '#4338CA' : '#9CA3AF',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: overridesActive ? 'pointer' : 'not-allowed',
                                padding: 0,
                            }}
                        >
                            Reset
                        </button>
                    </div>

                    <Field label="Paper size">
                        <select
                            value={value.paperSize ?? 'A4'}
                            onChange={(e) => {
                                const v = e.target.value as PdfPaperSize;
                                update({ paperSize: v === 'A4' ? undefined : v });
                            }}
                            style={selectStyle}
                        >
                            {PAPER_OPTIONS.map((p) => (
                                <option key={p} value={p}>
                                    {p} ({ROUND(PAPER_DIMENSIONS[p].width)} × {ROUND(PAPER_DIMENSIONS[p].height)} pt)
                                </option>
                            ))}
                        </select>
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                        <NumberField
                            label="Pad top"
                            placeholder={PADDING_DEFAULTS.paddingTop}
                            min={LAYOUT_BOUNDS.padding.min}
                            max={LAYOUT_BOUNDS.padding.max}
                            value={value.paddingTop}
                            onChange={(n) => update({ paddingTop: n })}
                        />
                        <NumberField
                            label="Pad bottom"
                            placeholder={PADDING_DEFAULTS.paddingBottom}
                            min={LAYOUT_BOUNDS.padding.min}
                            max={LAYOUT_BOUNDS.padding.max}
                            value={value.paddingBottom}
                            onChange={(n) => update({ paddingBottom: n })}
                        />
                        <NumberField
                            label="Pad left"
                            placeholder={PADDING_DEFAULTS.paddingLeft}
                            min={LAYOUT_BOUNDS.padding.min}
                            max={LAYOUT_BOUNDS.padding.max}
                            value={value.paddingLeft}
                            onChange={(n) => update({ paddingLeft: n })}
                        />
                        <NumberField
                            label="Pad right"
                            placeholder={PADDING_DEFAULTS.paddingRight}
                            min={LAYOUT_BOUNDS.padding.min}
                            max={LAYOUT_BOUNDS.padding.max}
                            value={value.paddingRight}
                            onChange={(n) => update({ paddingRight: n })}
                        />
                    </div>

                    <div style={{ marginTop: 10 }}>
                        <NumberField
                            label="Module gap (pack spacing)"
                            placeholder={DEFAULT_MODULE_GAP}
                            min={LAYOUT_BOUNDS.moduleGap.min}
                            max={LAYOUT_BOUNDS.moduleGap.max}
                            value={value.moduleGap}
                            onChange={(n) => update({ moduleGap: n })}
                        />
                    </div>

                    <div
                        style={{
                            marginTop: 12,
                            paddingTop: 10,
                            borderTop: '1px solid #F3F4F6',
                            fontSize: 11,
                            color: '#6B7280',
                            lineHeight: 1.45,
                        }}
                    >
                        Page: {ROUND(dim.width)} × {ROUND(dim.height)} pt. All units in PDF points (1 pt ≈ 0.353 mm). Empty fields use
                        defaults. Bounds: padding {LAYOUT_BOUNDS.padding.min}–{LAYOUT_BOUNDS.padding.max} pt, module gap{' '}
                        {LAYOUT_BOUNDS.moduleGap.min}–{LAYOUT_BOUNDS.moduleGap.max} pt.
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <label style={{ display: 'block' }}>
        <span style={labelStyle}>{label}</span>
        {children}
    </label>
);

const NumberField: React.FC<{
    label: string;
    placeholder: number;
    min: number;
    max: number;
    value: number | undefined;
    onChange: (n: number | undefined) => void;
}> = ({ label, placeholder, min, max, value, onChange }) => (
    <label style={{ display: 'block' }}>
        <span style={labelStyle}>{label}</span>
        <input
            type="number"
            min={min}
            max={max}
            placeholder={String(placeholder)}
            value={value ?? ''}
            onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') return onChange(undefined);
                const n = Number(raw);
                if (!Number.isFinite(n)) return onChange(undefined);
                onChange(n);
            }}
            style={inputStyle}
        />
    </label>
);

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 11,
    fontWeight: 500,
    color: '#6B7280',
    marginBottom: 4,
    letterSpacing: 0.1,
    textTransform: 'uppercase',
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 30,
    padding: '0 8px',
    border: '1px solid #D1D5DB',
    borderRadius: 5,
    fontSize: 12,
    color: '#111827',
    background: '#FFFFFF',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
    ...inputStyle,
    appearance: 'auto',
};
